/**
 * alien-product.js
 *
 * <alien-product-controller> reads sibling JSON bootstrap and resolves the first
 * in-stock child variant (metafield order) for the selected color × size.
 *
 * Cart-aware: /cart.js on init + cart:change. When a child's remaining qty hits 0
 * (deny policy), resolution rolls to the next child for the same color+size.
 */

const COLOR_ALIASES = ['color', 'colour', 'couleur'];
const SIZE_ALIASES = ['size', 'taille'];
const ALIGNMENTS = new Set(['start', 'center', 'end']);

class AlienProductController extends HTMLElement {
  connectedCallback() {
    if (this._isInitialized) return;

    this._config = this._parseBootstrap();
    if (!this._config) return;

    const { formId, colorOptionName, sizeOptionName, atcText, soldOutText, variants } = this._config;
    if (!Array.isArray(variants) || variants.length === 0) {
      console.warn('[alien-product] bootstrap variants missing or empty.');
      return;
    }

    this._variantRows = variants.filter(row => row && this._isNumericVariantId(row.variantId));
    this._knownVariantIds = new Set(this._variantRows.map(row => String(row.variantId)));
    if (!this._variantRows.length) return;

    this._colorName   = this._resolveOptionKey(colorOptionName, COLOR_ALIASES);
    this._sizeName    = this._resolveOptionKey(sizeOptionName, SIZE_ALIASES);
    this._addToCartText     = String(atcText ?? '');
    this._soldOutText = String(soldOutText ?? '');
    this._safeFormId  = String(formId ?? '').replace(/[^a-zA-Z0-9_-]/g, '');

    this._form         = document.getElementById(formId);
    this._variantInput = this._form?.querySelector('input[name="id"]');
    this._form?.setAttribute('data-alien-form', '');

    const commerceRoot = this.closest('.product-info');
    this._priceElement = commerceRoot?.querySelector('[data-alien-commerce="price"]') ?? null;
    this._skuElement   = commerceRoot?.querySelector('[data-alien-commerce="sku"]') ?? null;

    const rerender = this.closest('product-rerender');
    this._carousel = rerender?.querySelector('product-gallery .product-gallery__carousel') ?? null;
    this._hasAlienGallery  = !!this._carousel?.querySelector?.('.product-gallery__media[data-alien-color]');

    this._selection = { color: null, size: null };
    this._cartQuantities = null;
    this._abortController = new AbortController();

    this._onCartChange = e => {
      if (!e.detail?.cart) return;
      try {
        this._updateCartQuantities(e.detail.cart);
        this._commit(true, false);
      } catch (err) {
        console.warn('[alien-product] cart:change handler failed:', err);
      }
    };
    document.addEventListener('cart:change', this._onCartChange, { signal: this._abortController.signal });

    this._isInitialized = true;
    this._buildOptionUi();
    this._autoSelectInitial();
    this._loadCart();
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._isInitialized = false;
  }

  /* ─── Bootstrap ──────────────────────────────────────────────────────── */

  _bootstrapScript() {
    const prev = this.previousElementSibling;
    if (prev?.matches?.('[data-alien-product-bootstrap]')) return prev;
    return this.parentElement?.querySelector(':scope > [data-alien-product-bootstrap]') ?? null;
  }

  _parseBootstrap() {
    const el = this._bootstrapScript();
    if (!el) {
      console.warn('[alien-product] bootstrap script not found.');
      return null;
    }
    try {
      const data = JSON.parse(el.textContent.trim());
      return data && typeof data === 'object' ? data : null;
    } catch (err) {
      console.error('[alien-product] Failed to parse bootstrap JSON:', err);
      return null;
    }
  }

  /* ─── Cart ───────────────────────────────────────────────────────────── */

  async _loadCart() {
    try {
      const root = (typeof Shopify !== 'undefined' && Shopify.routes?.root) || '/';
      const cart = await fetch(`${root}cart.js`, { signal: this._abortController?.signal }).then(response => response.json());
      if (!this.isConnected) return;
      this._updateCartQuantities(cart);
      this._commit(true, false);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.warn('[alien-product] Could not fetch cart:', e);
    }
  }

  _updateCartQuantities(cart) {
    this._cartQuantities = new Map();
    for (const item of cart.items ?? []) {
      if (!this._isNumericVariantId(item.variant_id)) continue;
      const id = String(item.variant_id);
      this._cartQuantities.set(id, (this._cartQuantities.get(id) ?? 0) + Number(item.quantity || 0));
    }
  }

  _remainingQuantity(row) {
    if (!this._cartQuantities) return row.available ? Infinity : 0;
    if (row.inventoryPolicy === 'continue') return Infinity;
    const inCart = this._cartQuantities.get(String(row.variantId)) ?? 0;
    return Number(row.inventoryQuantity || 0) - inCart;
  }

  /* ─── Option helpers ─────────────────────────────────────────────────── */

  _resolveOptionKey(configured, aliases) {
    const row = this._variantRows.find(row => row.options && typeof row.options === 'object');
    if (!row) return configured;
    const keys = Object.keys(row.options);
    if (configured && keys.includes(configured)) return configured;
    const aliasSet = new Set((aliases || []).map(a => String(a).toLowerCase()));
    const want = String(configured || '').toLowerCase();
    if (want) aliasSet.add(want);
    return keys.find(k => {
      const kl = k.toLowerCase();
      return aliasSet.has(kl) || [...aliasSet].some(a => a && kl.includes(a));
    }) || configured;
  }

  /** T-Lab stores "English | French"; matching uses the first segment. */
  _canonicalOptionValue(value) {
    if (value == null) return null;
    const text = String(value).trim();
    if (!text) return null;
    const pipeIndex = text.indexOf('|');
    return (pipeIndex >= 0 ? text.slice(0, pipeIndex) : text).trim();
  }

  _optionLegend(name) {
    const s = String(name || '');
    if (!s.includes('|')) return s;
    const parts = s.split('|').map(p => p.trim()).filter(Boolean);
    return this._isFrLocale() ? (parts[1] || parts[0]) : parts[0];
  }

  _isFrLocale() {
    const lang = (document.documentElement.lang || '').toLowerCase();
    if (lang.startsWith('fr')) return true;
    return String(typeof Shopify !== 'undefined' ? Shopify.locale : '').toLowerCase().startsWith('fr');
  }

  _optionFromRow(row, configured, aliases) {
    const opts = row?.options || {};
    if (configured && opts[configured] != null && opts[configured] !== '') {
      return this._canonicalOptionValue(opts[configured]);
    }
    const aliasSet = new Set(
      [configured, ...(aliases || [])].filter(Boolean).map(a => String(a).toLowerCase())
    );
    for (const [optionKey, optionValue] of Object.entries(opts)) {
      const optionKeyLowercase = optionKey.toLowerCase();
      if (aliasSet.has(optionKeyLowercase) || [...aliasSet].some(a => a && optionKeyLowercase.includes(a))) {
        return this._canonicalOptionValue(optionValue);
      }
    }
    return null;
  }

  _colorFromRow(row) {
    const raw = row?.color || this._optionFromRow(row, this._colorName, COLOR_ALIASES);
    return this._canonicalOptionValue(raw) || this._canonicalOptionValue(Object.values(row?.options || {})[0]);
  }

  _sizeFromRow(row) {
    const raw = row?.size || this._optionFromRow(row, this._sizeName, SIZE_ALIASES);
    return this._canonicalOptionValue(raw) || this._canonicalOptionValue(Object.values(row?.options || {})[1]);
  }

  _sameOptionValue(a, b) {
    const ca = this._canonicalOptionValue(a);
    const cb = this._canonicalOptionValue(b);
    if (ca == null || cb == null) return false;
    return String(ca) === String(cb) || this._slug(ca) === this._slug(cb);
  }

  _isVisible(row) {
    const raw = String(row?.visibility ?? '').trim().toUpperCase();
    return raw.includes('SHOW') || raw.includes('AFFICHER');
  }

  _colorDisplay(color) {
    const matchingRow = this._variantRows.find(row => this._sameOptionValue(this._colorFromRow(row), color) && row.colorLabel);
    return String(matchingRow?.colorLabel || color || '').trim();
  }

  _uniqueBySlug(values) {
    const seen = new Set();
    return values.filter(value => {
      if (!value) return false;
      const key = this._slug(value);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  _uniqueColors() {
    return this._uniqueBySlug(
      this._variantRows.filter(row => this._isVisible(row)).map(row => this._colorFromRow(row))
    );
  }

  _isSelectable(row) {
    return this._isVisible(row) && this._remainingQuantity(row) > 0;
  }

  _variantRowsForColor(color, selectableOnly) {
    return this._variantRows.filter(row =>
      this._sameOptionValue(this._colorFromRow(row), color) &&
      (selectableOnly ? this._isSelectable(row) : this._isVisible(row))
    );
  }

  _colorHasSelectableVariant(color) {
    return this._variantRowsForColor(color, true).length > 0;
  }

  _allSizesForColor(color) {
    return this._uniqueBySlug(this._variantRowsForColor(color, false).map(row => this._sizeFromRow(row)));
  }

  _availableSizesForColor(color) {
    return this._uniqueBySlug(this._variantRowsForColor(color, true).map(row => this._sizeFromRow(row)));
  }

  _resolve(color, size) {
    return this._variantRowsForColor(color, true)
      .filter(row => this._sameOptionValue(this._sizeFromRow(row), size))
      .sort((a, b) => a.childIndex - b.childIndex)[0] ?? null;
  }

  /* ─── UI ─────────────────────────────────────────────────────────────── */

  _alignment() {
    const raw = this.dataset.alignment;
    return ALIGNMENTS.has(raw) ? raw : 'start';
  }

  _buildOptionUi() {
    const mount = this.querySelector('[data-alien-options]');
    if (!mount) return;

    mount.replaceChildren();
    const alignment = this._alignment();
    mount.append(
      this._buildRow(this._colorName, this._uniqueColors(), 'color', alignment),
      this._buildRow(this._sizeName, [], 'size', alignment)
    );
    mount.addEventListener('change', e => this._onInputChange(e), { signal: this._abortController.signal });
  }

  _buildRow(optionName, values, uiType, alignment) {
    const fieldset = document.createElement('fieldset');
    fieldset.className = `variant-picker__option justify-${alignment}`;

    const info = document.createElement('div');
    info.className = `variant-picker__option-info ${
      alignment === 'start' ? 'justify-between' : `justify-${alignment}`
    }`;

    const stack = document.createElement('div');
    stack.className = 'h-stack gap-2';

    const legend = document.createElement('legend');
    legend.className = 'text-subdued';
    legend.textContent = `${this._optionLegend(optionName)}:`;

    const labelEl = document.createElement('span');
    labelEl.dataset.alienLabel = this._slug(optionName);

    stack.append(legend, labelEl);
    info.append(stack);

    const valuesDiv = document.createElement('div');
    valuesDiv.className =
      uiType === 'color'
        ? 'variant-picker__option-values h-stack gap-3 wrap'
        : 'variant-picker__option-values h-stack gap-2 wrap';
    valuesDiv.dataset.alienValues = optionName;

    values.forEach((value, index) => {
      const available = uiType === 'color' ? this._colorHasSelectableVariant(value) : true;
      valuesDiv.append(this._buildButton(optionName, value, index, uiType, available));
    });

    fieldset.append(info, valuesDiv);
    return fieldset;
  }

  _buildButton(optionName, value, index, uiType, available) {
    const slug = this._slug(optionName);
    const id = `${this._safeFormId}-${slug}-${index}`;
    const frag = document.createDocumentFragment();
    const input = document.createElement('input');
    const label = document.createElement('label');
    const face = document.createElement('span');

    input.type = 'radio';
    input.className = 'sr-only';
    input.name = `${this._safeFormId}-alien-opt-${slug}`;
    input.value = value;
    input.id = id;
    input.dataset.alienOpt = optionName;
    label.htmlFor = id;

    if (uiType === 'color') {
      const display = this._colorDisplay(value);
      const swatchStyle = this._safeSwatchStyle(
        this._config.colorSwatches?.[value] ||
        this._config.colorSwatches?.[this._canonicalOptionValue(value)]
      );
      label.className = 'color-swatch color-swatch--circle color-swatch--sm' + (available ? '' : ' is-disabled');
      label.dataset.tooltip = display;
      label.dataset.tooltipStyle = 'color';
      if (swatchStyle) label.setAttribute('style', swatchStyle);
      face.className = 'sr-only';
      face.textContent = display;
    } else {
      label.className = `block-swatch block-swatch--outline${available ? '' : ' is-disabled'}`;
      face.textContent = value;
    }

    label.append(face);
    frag.append(input, label);
    return frag;
  }

  _safeSwatchStyle(style) {
    if (typeof style !== 'string') return '';
    const s = style.trim().slice(0, 500);
    if (!s.startsWith('--swatch-background:')) return '';
    const lower = s.toLowerCase();
    if (
      lower.includes('javascript:') ||
      lower.includes('expression(') ||
      lower.includes('behavior:') ||
      lower.includes('</')
    ) {
      return '';
    }
    return s;
  }

  /* ─── Selection ──────────────────────────────────────────────────────── */

  _autoSelectInitial() {
    const colors = this._uniqueColors();
    if (!colors.length) return;

    const urlRow = this._rowFromUrl();
    const color = urlRow
      ? this._colorFromRow(urlRow)
      : (colors.find(c => this._availableSizesForColor(c).length > 0) ?? colors[0]);

    this._selection.color = color;
    this._checkInput(this._colorName, color);
    this._setLabel(this._colorName, color);
    this._updateSizeRow(color);

    const size = urlRow
      ? this._sizeFromRow(urlRow)
      : (this._availableSizesForColor(color)[0] ?? null);
    if (size) {
      this._selection.size = size;
      this._checkInput(this._sizeName, size);
      this._setLabel(this._sizeName, size);
    }

    this._commit(true);
  }

  _rowFromUrl() {
    const id = this._urlVariantId();
    if (!id || !this._knownVariantIds.has(id)) return null;
    return this._variantRows.find(row => String(row.variantId) === id && this._isVisible(row)) ?? null;
  }

  _urlVariantId() {
    try {
      const raw = new URL(window.location.href).searchParams.get('variant');
      return this._isNumericVariantId(raw) ? String(raw) : null;
    } catch {
      return null;
    }
  }

  _syncUrl(row) {
    if (!this._isNumericVariantId(row?.variantId) || !this._knownVariantIds.has(String(row.variantId))) return;
    if (typeof history.replaceState !== 'function') return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(row.variantId));
      if (url.toString() !== window.location.href) {
        history.replaceState({ path: url.toString() }, '', url.toString());
      }
    } catch {
      /* ignore */
    }
  }

  _onInputChange(e) {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const optionName = input.dataset.alienOpt;
    if (!optionName) return;

    if (optionName === this._colorName) {
      this._selection.color = input.value;
      this._selection.size = null;
      this._setLabel(this._colorName, input.value);
      this._updateSizeRow(input.value);

      const autoSize = this._availableSizesForColor(input.value)[0] ?? null;
      if (autoSize) {
        this._selection.size = autoSize;
        this._checkInput(this._sizeName, autoSize);
        this._setLabel(this._sizeName, autoSize);
      }
      this._commit(true);
    } else if (optionName === this._sizeName) {
      this._selection.size = input.value;
      this._setLabel(this._sizeName, input.value);
      this._commit(false);
    }
  }

  _updateSizeRow(color) {
    const valuesDiv = this.querySelector(
      `[data-alien-values="${CSS.escape(this._sizeName)}"]`
    );
    if (!valuesDiv) return;

    valuesDiv.replaceChildren();
    const availSizes = new Set(this._availableSizesForColor(color));
    this._allSizesForColor(color).forEach((value, index) => {
      valuesDiv.append(this._buildButton(this._sizeName, value, index, 'size', availSizes.has(value)));
    });
  }

  /* ─── Commit ─────────────────────────────────────────────────────────── */

  _commit(instant = false, updateGallery = true) {
    const { color, size } = this._selection;
    if (color && updateGallery) this._filterAlienGalleryByColor(color);
    if (!color || !size) return;
    this._updateCommerce(this._resolve(color, size), instant, updateGallery);
  }

  _updateCommerce(row, instant = false, updateGallery = true) {
    const available = !!(row && this._remainingQuantity(row) > 0);
    const variantId = this._isNumericVariantId(row?.variantId) && this._knownVariantIds.has(String(row.variantId))
      ? String(row.variantId)
      : '';

    if (this._variantInput) this._variantInput.value = variantId;

    if (this._priceElement && row) {
      this._renderPrice(row);
    }

    if (this._skuElement) {
      const label = this._skuElement.dataset.skuLabel ?? 'SKU';
      this._skuElement.textContent = row?.sku ? `${label} ${this._asPlainText(row.sku)}` : '';
      this._skuElement.hidden = !row?.sku;
    }

    if (this._hasAlienGallery && row != null && updateGallery) {
      this._selectAlienGallerySlide(row, instant);
    }

    this._form?.querySelectorAll('button[type="submit"]').forEach(btn => {
      btn.disabled = !available;
      const text = available ? this._addToCartText : this._soldOutText;
      const content = btn.querySelector('button-content');
      if (content) {
        content.textContent = text;
        if (content.hasAttribute('rotated-text')) content.setAttribute('rotated-text', text);
        if (content.hasAttribute('up-text')) content.setAttribute('up-text', text);
      }
    });

    if (!updateGallery && this._selection.color) {
      this._updateSizeRow(this._selection.color);
      if (this._selection.size) {
        this._checkInput(this._sizeName, this._selection.size);
        this._setLabel(this._sizeName, this._selection.size);
      }
    }

    this._syncUrl(row);
  }

  _renderPrice(row) {
    const list = document.createElement('price-list');
    list.className = 'price-list justify-start';

    const price = document.createElement('div');
    price.className = 'h6';
    price.textContent = this._asPlainText(row.price);
    list.append(price);

    if (row.compareAtPrice) {
      const compare = document.createElement('div');
      compare.className = 'h7 line-through text-subdued';
      compare.textContent = this._asPlainText(row.compareAtPrice);
      list.append(compare);
    }

    this._priceElement.replaceChildren(list);
  }

  /* ─── Gallery ───────────────────────────────────────────────────────── */

  _filterAlienGalleryByColor(color) {
    if (!this._hasAlienGallery || !this._carousel) return;
    const want = this._slug(this._canonicalOptionValue(color) || '');

    const filter = el => {
      const token = el.dataset.alienColor ?? '';
      el.toggleAttribute('hidden', token !== '' && token !== want);
    };

    this._carousel.querySelectorAll('.product-gallery__media[data-alien-color]').forEach(filter);
    this._carousel.closest('product-gallery')
      ?.querySelectorAll('carousel-navigation .scroll-marker[data-alien-color]')
      .forEach(filter);
  }

  _selectAlienGallerySlide(row, instant) {
    if (!this._carousel || !row) return;
    const wantId = row.featuredMediaId != null ? String(row.featuredMediaId) : null;

    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      const cells = this._carousel.cells;
      if (!cells?.length) return;

      let idx = 0;
      if (wantId) {
        const found = cells.findIndex(c => c.dataset.mediaId === wantId);
        if (found >= 0) idx = found;
      } else if (typeof row.slideIndex === 'number' && row.slideIndex >= 0) {
        idx = Math.min(row.slideIndex, cells.length - 1);
      }

      if (idx in cells && typeof this._carousel.select === 'function') {
        this._carousel.select(idx, { instant: !!instant });
      }
    });
  }

  /* ─── Utilities ──────────────────────────────────────────────────────── */

  _isNumericVariantId(value) {
    return /^\d+$/.test(String(value ?? ''));
  }

  _asPlainText(value) {
    return new DOMParser().parseFromString(String(value ?? ''), 'text/html').documentElement.textContent || '';
  }

  _checkInput(optionName, value) {
    const inputName = `${this._safeFormId}-alien-opt-${this._slug(optionName)}`;
    const input = this.querySelector(
      `input[name="${CSS.escape(inputName)}"][value="${CSS.escape(value)}"]`
    );
    if (input) input.checked = true;
  }

  _setLabel(optionName, value) {
    const labelElement = this.querySelector(`[data-alien-label="${CSS.escape(this._slug(optionName))}"]`);
    if (!labelElement) return;
    labelElement.textContent = optionName === this._colorName ? this._colorDisplay(value) : (value ?? '');
  }

  _slug(name) {
    return String(name)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-{2,}/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}

if (!customElements.get('alien-product-controller')) {
  customElements.define('alien-product-controller', AlienProductController);
}
